import React, { useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

// Motion primitives. Everything here plays once, never blocks input, and degrades to a
// plain fade (or nothing) when the viewer asks for reduced motion.

const REDUCED = '(prefers-reduced-motion: reduce)';

export function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() => Boolean(window.matchMedia && window.matchMedia(REDUCED).matches));

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const query = window.matchMedia(REDUCED);
    const onChange = (event) => setReduced(event.matches);
    query.addEventListener ? query.addEventListener('change', onChange) : query.addListener(onChange);
    return () => {
      query.removeEventListener ? query.removeEventListener('change', onChange) : query.removeListener(onChange);
    };
  }, []);

  return reduced;
}

// Becomes true the first time the element scrolls into view, then stops observing.
export function useInView() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    if (!('IntersectionObserver' in window)) {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, inView];
}

// ------------------------------------------------------------------ reveal on scroll

const RevealBox = styled.div`
  opacity: 0;
  transform: translate3d(0, 28px, 0);
  transition:
    opacity 700ms ease var(--reveal-delay, 0ms),
    transform 1000ms var(--ease-out) var(--reveal-delay, 0ms);

  ${(p) =>
    p.$visible &&
    css`
      opacity: 1;
      transform: none;
    `}

  @media (prefers-reduced-motion: reduce) {
    transform: none;
    transition: opacity 300ms ease;
  }
`;

export function Reveal({ delay = 0, style, children, ...rest }) {
  const [ref, inView] = useInView();
  return (
    <RevealBox ref={ref} $visible={inView} style={{ ...style, '--reveal-delay': `${delay}ms` }} {...rest}>
      {children}
    </RevealBox>
  );
}

// ------------------------------------------------------------------ entrance on mount

const rise = keyframes`
  from {
    opacity: 0;
    transform: translate3d(0, 18px, 0);
    filter: blur(6px);
  }
  to {
    opacity: 1;
    transform: none;
    filter: blur(0);
  }
`;

const fade = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const riseIn = (delay = 0, duration = 900) => css`
  animation: ${rise} ${duration}ms var(--ease-out) ${delay}ms both;

  @media (prefers-reduced-motion: reduce) {
    animation: ${fade} 300ms ease both;
  }
`;

export const Rise = styled.div`
  ${(p) => riseIn(p.$delay || 0, p.$duration || 900)}
`;

const Word = styled.span`
  display: inline-block;
  ${(p) => riseIn(p.$delay, 1000)}
`;

// Headline that settles word by word.
export function RiseWords({ text, delay = 0, step = 70 }) {
  return text.split(' ').map((word, index) => (
    <React.Fragment key={`${word}-${index}`}>
      {index > 0 && ' '}
      <Word $delay={delay + index * step}>{word}</Word>
    </React.Fragment>
  ));
}

// ------------------------------------------------------------------ springs

// A small spring integrator in Apple's terms: `response` is roughly the settle time in
// seconds, `damping` 1 means no overshoot. Each key animates independently (decomposed X/Y),
// retargeting keeps the current position and velocity, so motion can be redirected mid-flight.
// `onFrame` receives the live values; write them straight to the DOM to avoid re-renders.
export function createSpring(initial, onFrame, { response = 0.4, damping = 1 } = {}) {
  const stiffness = Math.pow((2 * Math.PI) / response, 2);
  const friction = (4 * Math.PI * damping) / response;
  const state = {};
  Object.entries(initial).forEach(([key, value]) => {
    state[key] = { x: value, v: 0, target: value };
  });
  let frame = null;
  let last = 0;

  const values = () => {
    const out = {};
    Object.entries(state).forEach(([key, s]) => {
      out[key] = s.x;
    });
    return out;
  };

  const tick = (now) => {
    let remaining = Math.min(0.1, (now - last) / 1000);
    last = now;
    while (remaining > 0) {
      const h = Math.min(remaining, 1 / 240);
      remaining -= h;
      Object.values(state).forEach((s) => {
        const acceleration = -stiffness * (s.x - s.target) - friction * s.v;
        s.v += acceleration * h;
        s.x += s.v * h;
      });
    }
    let moving = false;
    Object.values(state).forEach((s) => {
      if (Math.abs(s.v) < 0.005 && Math.abs(s.x - s.target) < 0.005) {
        s.x = s.target;
        s.v = 0;
      } else {
        moving = true;
      }
    });
    onFrame(values());
    frame = moving ? requestAnimationFrame(tick) : null;
  };

  return {
    to(targets) {
      Object.entries(targets).forEach(([key, value]) => {
        if (!state[key]) state[key] = { x: value, v: 0, target: value };
        state[key].target = value;
      });
      if (!frame) {
        last = performance.now();
        frame = requestAnimationFrame(tick);
      }
    },
    jump(targets) {
      Object.entries(targets).forEach(([key, value]) => {
        state[key] = { x: value, v: 0, target: value };
      });
      onFrame(values());
    },
    stop() {
      if (frame) cancelAnimationFrame(frame);
      frame = null;
    },
  };
}

// ------------------------------------------------------------------ numbers

// Tweens toward `target`, always starting from the value currently on screen, so a change
// mid-flight redirects smoothly instead of jumping back to zero.
export function useAnimatedNumber(target, { duration = 1100, enabled = true } = {}) {
  const reduced = usePrefersReducedMotion();
  const [display, setDisplay] = useState(() => (enabled && !reduced ? 0 : target));
  const displayRef = useRef(display);

  useEffect(() => {
    if (!enabled) return undefined;
    if (reduced || !Number.isFinite(target)) {
      displayRef.current = target;
      setDisplay(target);
      return undefined;
    }
    const from = displayRef.current;
    const startedAt = performance.now();
    let frame;
    const tick = (now) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      const eased = 1 - Math.pow(1 - progress, 4);
      const value = from + (target - from) * eased;
      displayRef.current = value;
      setDisplay(value);
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, duration, enabled, reduced]);

  return display;
}

const integer = (value) => Math.round(value).toString();

export function AnimatedNumber({ value, format = integer, duration, start = true }) {
  const display = useAnimatedNumber(value, { duration, enabled: start });
  return format(display);
}
